document.documentElement.classList.remove('no-js');

class ZeronTheme {
  constructor() {
    this.bindMobileMenu();
    this.bindQuantityButtons();
    this.bindProductForms();
    this.bindProductMedia();
    this.bindStickyBuyBar();
    this.bindCartForm();
    this.bindCartLiveTotals();
    this.bindTransferDiscountPopup();
    this.bindSmartSearch();
  }

  /* ─── Mobile Menu ─── */
  bindMobileMenu() {
    document.querySelectorAll('[data-mobile-menu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = document.querySelector('[data-mobile-drawer]');
        if (!drawer) return;
        drawer.toggleAttribute('open');
        button.setAttribute('aria-expanded', drawer.hasAttribute('open') ? 'true' : 'false');
      });
    });
  }

  /* ─── Quantity Buttons (product page only, not cart) ─── */
  bindQuantityButtons() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-button]');
      if (!button) return;
      // Cart quantity handled separately — skip if inside cart form
      if (button.closest('#cart-form')) return;
      const wrapper = button.closest('[data-quantity]');
      const input = wrapper && wrapper.querySelector('input[type="number"]');
      if (!input) return;
      const step = button.dataset.quantityButton === 'plus' ? 1 : -1;
      const min = Number(input.getAttribute('min') || 1);
      const nextValue = Math.max(min, Number(input.value || min) + step);
      input.value = nextValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ─── Variant Picker ─── */
  bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const variantsScript = form.querySelector('[data-product-variants]');
      if (!variantsScript) return;
      let variants = [];
      try { variants = JSON.parse(variantsScript.textContent); } catch (e) { return; }

      const variantInput    = form.querySelector('[data-variant-id]');
      const priceNode       = document.querySelector(`[data-product-price="${form.dataset.productForm}"]`);
      const compareNode     = document.querySelector(`[data-product-compare="${form.dataset.productForm}"]`);
      const submitButton    = form.querySelector('[data-add-to-cart]');
      const stickyButton    = form.querySelector('[data-add-to-cart-sticky]');
      const stickyPrice     = form.querySelector('[data-sticky-price-target]');
      const statusNode      = form.querySelector('[data-variant-status]');

      const updateVariant = () => {
        const selected = Array.from(form.querySelectorAll('[data-option-position]')).map((fs) => {
          const checked = fs.querySelector('input[type="radio"]:checked');
          return checked ? checked.value : null;
        });
        const variant = variants.find((v) => v.options.every((val, i) => val === selected[i]));
        if (!variant) return;

        const fmt = this.formatMoney(variant.price, window.themeMoneyFormat || '{{amount}} TL');
        if (variantInput) variantInput.value = variant.id;
        if (priceNode)    priceNode.textContent = fmt;
        if (stickyPrice)  stickyPrice.textContent = fmt;

        if (compareNode) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareNode.textContent = this.formatMoney(variant.compare_at_price, window.themeMoneyFormat || '{{amount}} TL');
            compareNode.hidden = false;
          } else {
            compareNode.hidden = true;
          }
        }

        [submitButton, stickyButton].forEach((btn) => {
          if (!btn) return;
          btn.disabled = !variant.available;
          btn.textContent = variant.available
            ? (btn.dataset.availableText || 'Sepete ekle')
            : (btn.dataset.soldOutText || 'Tükendi');
        });

        if (statusNode) {
          statusNode.textContent = variant.available
            ? 'Stokta var, hemen sepete eklenebilir.'
            : 'Bu seçenek şu anda stokta yok.';
        }

        // Switch gallery to variant image — no DOM rebuild, just show matching slide
        if (variant.featured_media && variant.featured_media.id) {
          this.showGallerySlide(variant.featured_media.id);
        }
      };

      form.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) updateVariant();
      });
      updateVariant();
      this.bindAjaxAddToCart(form);
    });
  }

  /* ─── Product Media Gallery — instant, no DOM rebuild ─── */
  bindProductMedia() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const mainWrap  = gallery.querySelector('[data-main-media]');
    const thumbs    = Array.from(gallery.querySelectorAll('[data-media-thumb]'));
    if (!mainWrap || !thumbs.length) return;

    // Build all slides upfront and hide inactive ones
    const slides = [];

    thumbs.forEach((thumb, index) => {
      const template = thumb.querySelector('template');
      const mediaId  = thumb.dataset.mediaId;

      // Create slide div
      const slide = document.createElement('div');
      slide.className = 'gallery-slide';
      slide.dataset.mediaId = mediaId;
      slide.style.display = index === 0 ? 'block' : 'none';

      if (index === 0) {
        // First slide: use what's already rendered in mainWrap
        slide.innerHTML = mainWrap.innerHTML;
      } else if (template) {
        slide.innerHTML = template.innerHTML;
        // Eagerly set loading="lazy" → change to eager after first interaction
        slide.querySelectorAll('img').forEach((img) => {
          // Preload in background immediately so clicking feels instant
          if (img.dataset.src) img.src = img.dataset.src;
        });
      }

      slides.push({ slide, thumb, mediaId });
      mainWrap.appendChild(slide);
    });

    // Clear original first-slide content (now in slide[0])
    // Remove direct children that are NOT gallery-slide divs
    Array.from(mainWrap.childNodes).forEach((node) => {
      if (node.nodeType === 1 && !node.classList.contains('gallery-slide')) {
        node.remove();
      }
    });

    // Mark first thumb active
    if (thumbs[0]) thumbs[0].classList.add('is-active');

    // Click handler — just toggle display, no innerHTML ops
    const activateSlide = (targetMediaId) => {
      slides.forEach(({ slide, thumb, mediaId }) => {
        const isActive = mediaId === targetMediaId;
        slide.style.display = isActive ? 'block' : 'none';
        thumb.classList.toggle('is-active', isActive);
        if (isActive) {
          // Ensure image is loaded (force eager)
          slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
            img.loading = 'eager';
          });
        }
      });
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        activateSlide(thumb.dataset.mediaId);
      });
    });

    // Expose for variant picker
    this._activateSlide = activateSlide;
  }

  showGallerySlide(mediaId) {
    if (this._activateSlide) this._activateSlide(String(mediaId));
  }

  /* ─── Sticky Buy Bar ─── */
  bindStickyBuyBar() {
    const bar = document.querySelector('[data-sticky-buy-bar]');
    if (!bar) return;
    const toggle = () => {
      const shouldShow = window.scrollY > 520 && window.matchMedia('(max-width: 760px)').matches;
      bar.classList.toggle('is-visible', shouldShow);
    };
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    window.addEventListener('resize', toggle);
  }

  /* ─── Cart Form (checkout button) ─── */
  bindCartForm() {
    const cartForm = document.querySelector('#cart-form');
    if (!cartForm) return;

    const checkoutButton = cartForm.querySelector('[data-cart-checkout]');
    if (checkoutButton) {
      checkoutButton.addEventListener('click', () => {
        cartForm.setAttribute('action', window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart` : '/cart');
        let hidden = cartForm.querySelector('input[name="checkout"][type="hidden"]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type  = 'hidden';
          hidden.name  = 'checkout';
          hidden.value = 'checkout';
          cartForm.appendChild(hidden);
        }
      });
    }

    const updateButton = cartForm.querySelector('[data-cart-update]');
    if (updateButton) {
      updateButton.addEventListener('click', () => {
        cartForm.setAttribute('action', window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart` : '/cart');
      });
    }
  }

  /* ─── Cart Live Totals — debounced, no double-event conflict ─── */
  bindCartLiveTotals() {
    const cartForm = document.querySelector('#cart-form');
    if (!cartForm) return;

    const inputs = cartForm.querySelectorAll('[data-cart-quantity-input]');
    if (!inputs.length) return;

    const moneyFormat = window.themeMoneyFormat || '{{amount}} TL';
    const note        = cartForm.querySelector('[data-cart-live-note]');
    const timers      = new Map();
    const root        = window.Shopify?.routes?.root || '/';
    const changeUrl   = `${root}cart/change.js`;

    const setNote = (text, ms = 0) => {
      if (!note) return;
      note.textContent = text || '';
      note.classList.toggle('is-visible', Boolean(text));
      if (ms) window.clearTimeout(this._noteTimer), this._noteTimer = window.setTimeout(() => setNote(''), ms);
    };

    const updateHeaderCount = (cart) => {
      document.querySelectorAll('.cart-count').forEach((n) => { n.textContent = cart.item_count; });
    };

    // Optimistic: recalculate totals from current input values instantly
    const optimisticUpdate = () => {
      let total = 0;
      cartForm.querySelectorAll('[data-cart-item]').forEach((row) => {
        const input     = row.querySelector('[data-cart-quantity-input]');
        const linePrice = row.querySelector('[data-cart-line-price]');
        const unitPrice = Number(row.dataset.unitPrice || 0);
        const qty       = Math.max(Number(input?.getAttribute('min') || 0), Number(input?.value || 0));
        const lineTotal = unitPrice * qty;
        total += lineTotal;
        if (linePrice) linePrice.textContent = this.formatMoney(lineTotal, moneyFormat);
      });
      cartForm.querySelectorAll('[data-cart-subtotal],[data-cart-total]').forEach((n) => {
        n.textContent = this.formatMoney(total, moneyFormat);
      });
    };

    const renderAjaxCart = (cart) => {
      if (!cart || !Array.isArray(cart.items)) return;
      const liveKeys = new Set(cart.items.map((i) => i.key));

      cart.items.forEach((item) => {
        const safe    = window.CSS?.escape ? CSS.escape(item.key) : item.key.replace(/"/g, '\\"');
        const row     = cartForm.querySelector(`[data-cart-item-key="${safe}"][data-cart-item]`);
        if (!row) return;
        const input   = row.querySelector('[data-cart-quantity-input]');
        const linePrice = row.querySelector('[data-cart-line-price]');
        row.dataset.unitPrice = item.final_price;
        if (input && Number(input.value) !== item.quantity) input.value = item.quantity;
        if (linePrice) linePrice.textContent = this.formatMoney(item.final_line_price, moneyFormat);
      });

      // Remove zero-qty rows
      cartForm.querySelectorAll('[data-cart-item]').forEach((row) => {
        const key   = row.dataset.cartItemKey;
        const input = row.querySelector('[data-cart-quantity-input]');
        if (key && !liveKeys.has(key) && Number(input?.value || 0) === 0) row.remove();
      });

      cartForm.querySelectorAll('[data-cart-subtotal],[data-cart-total]').forEach((n) => {
        n.textContent = this.formatMoney(cart.total_price, moneyFormat);
      });
      updateHeaderCount(cart);
      setNote('Sepet güncellendi.', 1800);

      if (cart.item_count === 0) window.location.reload();
    };

    // Sync to server — debounced 400 ms, with loading indicator per row
    const syncCart = (input) => {
      const key = input.dataset.cartItemKey;
      if (!key) return;
      const qty = Math.max(Number(input.getAttribute('min') || 0), Number(input.value || 0));
      input.value = qty;
      optimisticUpdate();

      // Show spinner on the row
      const row = input.closest('[data-cart-item]');
      if (row) row.dataset.loading = 'true';
      setNote('Sepet güncelleniyor…');

      window.clearTimeout(timers.get(key));
      timers.set(key, window.setTimeout(() => {
        fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        })
          .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
          .then((cart) => {
            if (row) delete row.dataset.loading;
            renderAjaxCart(cart);
          })
          .catch(() => {
            if (row) delete row.dataset.loading;
            setNote('Güncelleme başarısız, sayfayı yenileyin.', 3000);
          });
      }, 400));
    };

    // Only listen to 'change' (fired by +/- buttons after value set), NOT 'input'
    // This avoids double-firing when user types quickly
    inputs.forEach((input) => {
      // Cart +/- buttons
      const wrap = input.closest('[data-quantity]');
      if (wrap) {
        wrap.querySelectorAll('[data-quantity-button]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const step = btn.dataset.quantityButton === 'plus' ? 1 : -1;
            const min  = Number(input.getAttribute('min') || 0);
            input.value = Math.max(min, Number(input.value || min) + step);
            syncCart(input);
          });
        });
      }
      // Direct input change (keyboard)
      let inputTimer;
      input.addEventListener('input', () => {
        optimisticUpdate(); // instant visual feedback
        window.clearTimeout(inputTimer);
        inputTimer = window.setTimeout(() => syncCart(input), 600);
      });
    });
  }

  /* ─── Transfer Discount Popup ─── */
  bindTransferDiscountPopup() {
    const storageKey = 'zeron-transfer-discount-popup-closed';
    try { if (window.sessionStorage && sessionStorage.getItem(storageKey) === '1') return; } catch (e) {}

    const showPopup = () => {
      if (document.querySelector('[data-zeron-transfer-popup]')) return;
      const popup = document.createElement('div');
      popup.className = 'zeron-popup';
      popup.setAttribute('data-zeron-transfer-popup', '');
      popup.innerHTML = `
        <div class="zeron-popup__backdrop" data-zeron-popup-close></div>
        <div class="zeron-popup__dialog" role="dialog" aria-modal="true" aria-labelledby="zeron-transfer-title">
          <button class="zeron-popup__close" type="button" aria-label="Kapat" data-zeron-popup-close>×</button>
          <div class="zeron-popup__badge">Sadece havale/EFT için</div>
          <h2 id="zeron-transfer-title">Havale ödemede %10 indirim!</h2>
          <p>Ödemeyi havale/EFT ile tamamlayan müşterilerimize sepet tutarı üzerinden <strong>%10 ekstra indirim</strong> uygulanır.</p>
          <div class="zeron-popup__phone">WhatsApp destek: <a href="https://wa.me/905312093794" target="_blank" rel="noopener">0531 209 37 94</a></div>
          <div class="zeron-popup__actions">
            <a class="button button--full" href="https://wa.me/905312093794?text=Merhaba%2C%20havale%20ile%20%2510%20indirim%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum." target="_blank" rel="noopener">WhatsApp'tan bilgi al</a>
            <button class="button button--secondary button--full" type="button" data-zeron-popup-close>Alışverişe devam et</button>
          </div>
        </div>`;
      document.body.appendChild(popup);
      document.body.classList.add('zeron-popup-open');

      const closePopup = () => {
        popup.classList.add('is-closing');
        document.body.classList.remove('zeron-popup-open');
        try { if (window.sessionStorage) sessionStorage.setItem(storageKey, '1'); } catch (e) {}
        window.setTimeout(() => popup.remove(), 180);
        document.removeEventListener('keydown', onKey);
      };
      const onKey = (e) => { if (e.key === 'Escape') closePopup(); };
      popup.querySelectorAll('[data-zeron-popup-close]').forEach((b) => b.addEventListener('click', closePopup));
      document.addEventListener('keydown', onKey);
    };

    window.setTimeout(showPopup, 1400);
  }

  /* ─── Smart Search ─── */
  bindSmartSearch() {
    const normalize = (v) => String(v || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i')
      .trim();

    const routes = [
      { url: '/search?q=MANGAL%20TEL%C4%B0', words: ['aksesuar', 'mangal teli', 'tel', 'komur', 'kömür', 'charcoal'] },
      { url: '/search?q=bioethanol', words: ['somine', 'şömine', 'fireplace', 'bioethanol', 'biyoetanol'] },
      { url: '/search?q=kamp', words: ['kamp', 'camping', 'outdoor', 'piknik'] },
      { url: '/search?q=mangal', words: ['mangal', 'grill', 'barbeku', 'barbekü', 'bbq'] }
    ];

    document.querySelectorAll('[data-zeron-smart-search]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        const input = form.querySelector('input[name="q"]');
        if (!input) return;
        const query = normalize(input.value);
        if (!query) return;
        const match = routes.find((r) => r.words.some((w) => query.includes(normalize(w))));
        if (!match) return;
        e.preventDefault();
        window.location.href = match.url;
      });
    });
  }

  /* ─── AJAX Add to Cart ─── */
  bindAjaxAddToCart(form) {
    form.addEventListener('submit', (e) => {
      const submitter = e.submitter || document.activeElement;
      if (!submitter || submitter.getAttribute('name') !== 'add') return;
      e.preventDefault();
      this.addProductToCart(form, submitter);
    });
  }

  addProductToCart(form, submitter) {
    const root = window.Shopify?.routes?.root || '/';
    const originalText = submitter.textContent;

    submitter.disabled = true;
    submitter.textContent = 'Ekleniyor…';

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((item) => {
        this.refreshCartCount();
        this.showCartWarningModal(item);
      })
      .catch(() => {
        submitter.disabled = false;
        submitter.textContent = originalText;
        HTMLFormElement.prototype.submit.call(form);
      })
      .finally(() => {
        window.setTimeout(() => {
          submitter.disabled = false;
          submitter.textContent = originalText;
        }, 600);
      });
  }

  refreshCartCount() {
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.ok ? r.json() : null)
      .then((cart) => {
        if (!cart) return;
        document.querySelectorAll('.cart-count').forEach((n) => { n.textContent = cart.item_count; });
      })
      .catch(() => {});
  }

  showCartWarningModal(item) {
    const existing = document.querySelector('[data-zeron-cart-warning]');
    if (existing) existing.remove();

    const root  = window.Shopify?.routes?.root || '/';
    const title = item?.product_title || 'Ürün';
    const modal = document.createElement('div');
    modal.className = 'zeron-cart-warning';
    modal.setAttribute('data-zeron-cart-warning', '');
    modal.innerHTML = `
      <div class="zeron-cart-warning__backdrop" data-zeron-cart-close></div>
      <div class="zeron-cart-warning__dialog" role="dialog" aria-modal="true" aria-labelledby="zeron-cart-title">
        <button class="zeron-cart-warning__close" type="button" aria-label="Kapat" data-zeron-cart-close>×</button>
        <div class="zeron-cart-warning__check">✓</div>
        <p class="zeron-cart-warning__mini">Sepete eklendi</p>
        <h2 id="zeron-cart-title">${this.escapeHtml(title)} sepete eklendi.</h2>
        <div class="zeron-cart-warning__notice">
          <strong>Önemli bilgilendirme</strong>
          <p>Kargoyu teslim alamayacaksanız lütfen siparişi tamamlamayınız. Teslim alınmayan kargolarda gidiş-dönüş kargo bedeli oluşabilir.</p>
        </div>
        <p class="zeron-cart-warning__support">Yardım için WhatsApp: <a href="https://wa.me/905312093794" target="_blank" rel="noopener">0531 209 37 94</a></p>
        <div class="zeron-cart-warning__actions">
          <a class="button button--full" href="${root}cart">Sepete git</a>
          <button class="button button--secondary button--full" type="button" data-zeron-cart-close>Alışverişe devam et</button>
        </div>
      </div>`;

    document.body.appendChild(modal);
    document.body.classList.add('zeron-cart-warning-open');

    const closeModal = () => {
      modal.classList.add('is-closing');
      document.body.classList.remove('zeron-cart-warning-open');
      window.setTimeout(() => modal.remove(), 180);
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    modal.querySelectorAll('[data-zeron-cart-close]').forEach((b) => b.addEventListener('click', closeModal));
    document.addEventListener('keydown', onKey);
  }

  escapeHtml(v) {
    const d = document.createElement('div');
    d.textContent = v == null ? '' : String(v);
    return d.innerHTML;
  }

  formatMoney(cents, format) {
    const value = (Number(cents || 0) / 100).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return format.replace('{{amount}}', value).replace('{{ amount }}', value);
  }
}

window.addEventListener('DOMContentLoaded', () => new ZeronTheme());
